import { RedeemCodes, Module } from "@apis/index";
import Header from "@commons/header";
import Button from "@components/button";
import Table from "@components/table";
import { RootState } from "@store/index";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Wrapper from "@components/wrapper";
import { useNavigate } from "react-router-dom";
import { useModal } from "@context/ModalContext";
import GenerateBatchModal from "./GenerateBatchModal";
import ExportProgressModal from "./ExportProgressModal";

const RedeemCodesTable = () => {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const {
    items: redeemCodes = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.redeemCodes || { items: [] });
  const { data: modules = [] } = useSelector(
    (state: RootState) => state.modules,
  );

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [batches, setBatches] = useState<string[]>([]);
  const [batchName, setBatchName] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isAllSelectedAcrossPages, setIsAllSelectedAcrossPages] =
    useState(false);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await RedeemCodes.getBatches();
      setBatches(res || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchRedeemCodes = useCallback(async () => {
    await RedeemCodes.list({
      state: "redeemCodes",
      params: {
        page,
        limit: 30,
        search,
        status,
        moduleId,
        batchName,
        ...dateRange,
      },
    });
  }, [page, search, status, moduleId, batchName, dateRange]);

  useEffect(() => {
    fetchRedeemCodes();
  }, [fetchRedeemCodes]);

  useEffect(() => {
    Module.list({ state: "modules", params: { isActive: true } });
    fetchBatches();
  }, [fetchBatches]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0 && !isAllSelectedAcrossPages) return;

    const count = isAllSelectedAcrossPages ? totalItems : selectedIds.length;
    if (!window.confirm(`Are you sure you want to delete ${count} codes?`))
      return;

    const promise = RedeemCodes.bulkDelete(
      isAllSelectedAcrossPages
        ? { filters: { search, status, moduleId, ...dateRange } }
        : { ids: selectedIds },
    );

    await toast.promise(promise, {
      pending: "Deleting codes...",
      success: "Deleted successfully!",
      error: "Error deleting codes",
    });
    setSelectedIds([]);
    setIsAllSelectedAcrossPages(false);
    fetchRedeemCodes();
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === redeemCodes.length && redeemCodes.length > 0) {
      setSelectedIds([]);
      setIsAllSelectedAcrossPages(false);
    } else {
      setSelectedIds(redeemCodes.map((item: any) => item.id));
    }
  };

  const toggleSelect = (id: number) => {
    setIsAllSelectedAcrossPages(false);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const columns: any = [
    {
      title: (
        <input
          type="checkbox"
          checked={
            selectedIds.length === redeemCodes.length && redeemCodes.length > 0
          }
          onChange={toggleSelectAll}
        />
      ),
      key: "selection",
      render: (item: any) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(item.id)}
          onChange={() => toggleSelect(item.id)}
        />
      ),
    },
    {
      title: "S.N.",
      key: "sn",
      render: (_: any, index: number) => (page - 1) * 30 + index,
    },
    {
      title: "Date Created",
      key: "createdAt",
      render: (item: any) => new Date(item.createdAt).toLocaleString(),
    },
    {
      title: "Code",
      key: "code",
      render: (item: any) => <strong>{item.code}</strong>,
    },
    {
      title: "Batch Name",
      key: "batchName",
      render: (item: any) => item.batchName || "-",
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => (
        <span style={{ color: item.status === "Used" ? "red" : "green" }}>
          {item.status}
        </span>
      ),
    },
    {
      title: "Date Used",
      key: "usedAt",
      render: (item: any) =>
        item.usedAt ? new Date(item.usedAt).toLocaleString() : "-",
    },
    {
      title: "Candidate Name",
      key: "candidateName",
      render: (item: any) => item.candidateName || "-",
    },
    {
      title: "Candidate Phone",
      key: "candidatePhone",
      render: (item: any) => item.candidatePhone || "-",
    },
    {
      title: "Order ID",
      key: "orderId",
      render: (item: any) => item.orderId || "-",
    },
    {
      title: "Order Amount",
      key: "orderAmount",
      render: (item: any) =>
        item.orderAmount ? `Rs. ${item.orderAmount}` : "-",
    },
    {
      title: "Order Module",
      key: "orderModule",
      render: (item: any) => item.orderModule || "-",
    },
    {
      title: "Order Zone",
      key: "orderZone",
      render: (item: any) => item.orderZone || "-",
    },
  ];

  const handleDownload = async () => {
    try {
      const response: any = await RedeemCodes.exportExcel({
        search,
        status,
        moduleId,
        batchName,
        ...dateRange,
      });
      const jobId = response.jobId;

      if (!jobId) {
        toast.error("Failed to start export");
        return;
      }

      showModal(<ExportProgressModal jobId={jobId} />, { width: 450 });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <Wrapper>
      <Header title="Redeem Codes Management" />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "20px",
          alignItems: "center",
          background: "#f8f9fa",
          padding: "15px",
          borderRadius: "8px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            Search
          </label>
          <input
            type="text"
            placeholder="Code, Name, or Phone"
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              minWidth: "200px",
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            Status
          </label>
          <select
            onChange={(e) => setStatus(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              minWidth: "150px",
              background: "white",
            }}
          >
            <option value="">All Status</option>
            <option value="Used">Used</option>
            <option value="Not Used">Not Used</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            Module
          </label>
          <select
            onChange={(e) => setModuleId(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              minWidth: "150px",
              background: "white",
            }}
          >
            <option value="">All Modules</option>
            {modules.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            Batch Name
          </label>
          <select
            onChange={(e) => setBatchName(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              minWidth: "180px",
              background: "white",
            }}
          >
            <option value="">All Batches</option>
            {batches.map((batch: string) => (
              <option key={batch} value={batch}>
                {batch}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            Start Date
          </label>
          <input
            type="date"
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
            }
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <label
            style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}
          >
            End Date
          </label>
          <input
            type="date"
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
            }
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
            }}
          />
        </div>

        <div
          style={{
            marginLeft: "auto",
            alignSelf: "flex-end",
            display: "flex",
            gap: "10px",
          }}
        >
          {(selectedIds.length > 0 || isAllSelectedAcrossPages) && (
            <Button
              title={`Remove (${isAllSelectedAcrossPages ? totalItems : selectedIds.length})`}
              onClick={handleBulkDelete}
              style={{ height: "40px", background: "#dc3545" }}
            />
          )}
          <Button
            title="Download Excel"
            onClick={handleDownload}
            style={{ height: "40px", background: "#28a745" }}
          />
          <Button
            title="Generate 10K Codes"
            onClick={() =>
              showModal(
                <GenerateBatchModal
                  onSuccess={() => {
                    fetchRedeemCodes();
                    fetchBatches();
                  }}
                />,
                {
                  width: 600,
                },
              )
            }
            style={{ height: "40px" }}
          />
        </div>
      </div>

      {selectedIds.length === redeemCodes.length &&
        totalItems > redeemCodes.length && (
          <div
            style={{
              background: "#fff9db",
              padding: "10px",
              textAlign: "center",
              borderRadius: "8px",
              marginBottom: "15px",
              border: "1px solid #ffe066",
              fontSize: "14px",
            }}
          >
            {isAllSelectedAcrossPages ? (
              <span>
                All <b>{totalItems}</b> codes are selected.{" "}
                <button
                  onClick={() => setIsAllSelectedAcrossPages(false)}
                  style={{
                    color: "#228be6",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  Clear selection
                </button>
              </span>
            ) : (
              <span>
                All <b>{redeemCodes.length}</b> codes on this page are selected.{" "}
                <button
                  onClick={() => setIsAllSelectedAcrossPages(true)}
                  style={{
                    color: "#228be6",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  Select all {totalItems} codes
                </button>
              </span>
            )}
          </div>
        )}

      {/* Modal removed, handled by useModal */}

      <Table
        data={redeemCodes}
        columns={columns}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
        itemsPerPage={30}
      />
    </Wrapper>
  );
};

export default RedeemCodesTable;
